import { http } from '../http'
import type { Paginated } from '@/types/api'
import type {
  AdminMenuItem,
  ListMenuItemsParams,
  MenuItem,
  MenuItemPayload,
  MenuOption,
} from '@/types/menu'

/**
 * `GET /menu` — public, but personalised when a token is sent.
 *
 * The backend filters the tree by global role, role in the *selected* group and
 * active subscriptions, so this must be re-fetched whenever the selected group
 * changes or the user logs in/out.
 */
export function getMenu() {
  return http.get<MenuItem[]>('/menu').then((response) => response.data)
}

/* ------------------------------ admin panel ------------------------------- */

/**
 * axios drops a `null`-valued param from the query string entirely (it never
 * reaches the wire), but the backend's "top level only" filter is triggered by
 * `parent_id` being explicitly present as `null` (`sometimes|nullable`) — an
 * absent key just skips the filter. Send `''` instead: `ConvertEmptyStringsToNull`
 * turns it back into `null` server-side before validation runs. Same shape of
 * fix as `normalizeBooleanParams` in `servers.ts`.
 */
function normalizeMenuParams(params: ListMenuItemsParams): Record<string, unknown> {
  const { parent_id, ...rest } = params
  const query: Record<string, unknown> = { ...rest }
  if (parent_id !== undefined) query.parent_id = parent_id === null ? '' : parent_id
  return query
}

/**
 * `GET /menu/manage` — admin only, paginated.
 *
 * Deliberately not the same data as `getMenu()`: this is the raw table, with
 * every entry and every flag, because an admin must be able to fix a permission
 * that currently hides the entry from them.
 */
export function listMenuItems(params: ListMenuItemsParams = {}) {
  return http
    .get<Paginated<AdminMenuItem>>('/menu/manage', { params: normalizeMenuParams(params) })
    .then((response) => response.data)
}

/** `GET /menu/options` — every entry in tree order, for the parent dropdown. */
export function getMenuOptions() {
  return http.get<MenuOption[]>('/menu/options').then((response) => response.data)
}

/** `POST /menu` — `label` is the only required field. */
export function createMenuItem(payload: MenuItemPayload) {
  return http.post<AdminMenuItem>('/menu', payload).then((response) => response.data)
}

/**
 * `PUT /menu/{id}` — accepts fields *and* permission flags, so one save is
 * enough (no follow-up PATCH on `/permissions`).
 */
export function updateMenuItem(id: number, payload: MenuItemPayload) {
  return http.put<AdminMenuItem>(`/menu/${id}`, payload).then((response) => response.data)
}

/** `DELETE /menu/{id}` — the children of the entry go with it (FK cascade). */
export function deleteMenuItem(id: number) {
  return http.delete<AdminMenuItem>(`/menu/${id}`).then((response) => response.data)
}

/**
 * `DELETE /menu` — multi-select delete. Same cascade caveat: removing a parent
 * also removes children that were never selected.
 */
export function deleteMenuItems(ids: number[]) {
  return http
    .delete<{ deleted: number }>('/menu', { data: { ids } })
    .then((response) => response.data)
}
